import { generateId } from '../id';
import {
  addEntity,
  getStore,
  getEntitiesByWorkspace,
  updateEntity,
  deleteEntity,
  getEntity,
} from '../store';
import type { TeamMember, TeamRole } from '../types';

export function createTeamMember(input: {
  workspaceId: string;
  name: string;
  email: string;
  role: TeamRole;
  avatar?: string;
  title?: string;
  department?: string;
}): TeamMember {
  const now = new Date();
  const member: TeamMember = {
    id: generateId('member'),
    workspaceId: input.workspaceId,
    name: input.name,
    email: input.email,
    role: input.role,
    avatar: input.avatar,
    title: input.title,
    department: input.department,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
  return addEntity(getStore().teamMembers, member);
}

export function getTeamMemberById(id: string): TeamMember | undefined {
  return getEntity(getStore().teamMembers, id);
}

export function getTeamMembersByWorkspace(workspaceId: string): TeamMember[] {
  return getEntitiesByWorkspace(getStore().teamMembers, workspaceId);
}

export function getTeamMembersByRole(workspaceId: string, role: TeamRole): TeamMember[] {
  return getTeamMembersByWorkspace(workspaceId).filter((m) => m.role === role);
}

export function getActiveTeamMembers(workspaceId: string): TeamMember[] {
  return getTeamMembersByWorkspace(workspaceId).filter((m) => m.isActive);
}

export function updateTeamMember(
  id: string,
  updates: Partial<Omit<TeamMember, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt'>>
): TeamMember | undefined {
  return updateEntity(getStore().teamMembers, id, updates);
}

export function updateTeamMemberRole(id: string, role: TeamRole): TeamMember | undefined {
  return updateEntity(getStore().teamMembers, id, { role });
}

export function deactivateTeamMember(id: string): TeamMember | undefined {
  return updateEntity(getStore().teamMembers, id, { isActive: false });
}

export function deleteTeamMember(id: string): boolean {
  return deleteEntity(getStore().teamMembers, id);
}

export function getTeamStats(workspaceId: string): {
  total: number;
  active: number;
  byRole: Record<TeamRole, number>;
} {
  const members = getTeamMembersByWorkspace(workspaceId);
  const roles: TeamRole[] = ['team_lead', 'developer', 'planner', 'qa', 'member'];

  return {
    total: members.length,
    active: members.filter((m) => m.isActive).length,
    byRole: roles.reduce((acc, role) => {
      acc[role] = members.filter((m) => m.role === role).length;
      return acc;
    }, {} as Record<TeamRole, number>),
  };
}

// Generate demo team members for a workspace
export function generateDemoTeamMembers(workspaceId: string): TeamMember[] {
  const demoMembers = [
    {
      name: '김민수',
      email: 'minsu.kim@company.com',
      role: 'team_lead' as TeamRole,
      title: 'Engineering Manager',
      department: 'Engineering',
    },
    {
      name: '이지영',
      email: 'jiyoung.lee@company.com',
      role: 'developer' as TeamRole,
      title: 'Senior Developer',
      department: 'Engineering',
    },
    {
      name: '박서현',
      email: 'seohyun.park@company.com',
      role: 'planner' as TeamRole,
      title: 'Product Manager',
      department: 'Product',
    },
    {
      name: '최준호',
      email: 'junho.choi@company.com',
      role: 'qa' as TeamRole,
      title: 'QA Engineer',
      department: 'Quality',
    },
    {
      name: '정유진',
      email: 'yujin.jung@company.com',
      role: 'developer' as TeamRole,
      title: 'Frontend Developer',
      department: 'Engineering',
    },
    {
      name: '한소라',
      email: 'sora.han@company.com',
      role: 'member' as TeamRole,
      title: 'Junior Developer',
      department: 'Engineering',
    },
  ];

  const existing = getTeamMembersByWorkspace(workspaceId);
  if (existing.length > 0) return existing;

  return demoMembers.map((m) =>
    createTeamMember({
      workspaceId,
      ...m,
    })
  );
}
